/**
 * What each species looks like.
 *
 * One faceted, vertex-coloured geometry per species, merged from a few
 * primitives, sized for an instance scale of 1 and standing on its origin.
 * Colour lives in the vertices — brown trunk, green crown, white snow on the
 * upward faces of a snowy spruce — so each species is one instanced mesh and
 * one draw call per tile. The per-instance colour only nudges the hue, which
 * is enough to stop a stand of trees reading as clones.
 */

import type { BufferGeometry, InstancedMesh as InstancedMeshType, Material } from 'three';
import {
  BoxGeometry,
  BufferAttribute,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  DynamicDrawUsage,
  Float32BufferAttribute,
  BufferGeometry as Geometry,
  IcosahedronGeometry,
  InstancedMesh,
  MeshDepthMaterial,
  MeshStandardMaterial,
  Object3D,
  AdditiveBlending,
  PlaneGeometry,
  ShaderMaterial,
  Sphere,
  Vector2,
  Vector3,
} from 'three';
import type { WebGLProgramParametersWithUniforms } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Species } from '../core/biome/flora.js';
import { PROPS, SPECIES, SPECIES_ID, STRETCHED } from '../core/biome/flora.js';
import type { WindState } from '../core/weather/wind.js';
import { BEND_SPEED, MAX_BEND } from '../core/weather/wind.js';
import { bakeModel } from './bake.js';
import {
  GLSL_LAMP_LIGHT,
  GLSL_SNOW_TOP,
  SNOW_TOP_COLOUR,
  addStreetLampObjectShader,
  applyStreetLampLight,
  floraNight,
  lampLight,
  terrainWeather,
  updateLampLightMap,
} from './terrainMaterial.js';
import type { CarKind } from './Vehicle.js';
import { createParkedCar, createVehicle, headlamp, isBus, isTruck, taillamp } from './Vehicle.js';
import { createMotorbike, createParkedMotorbike, headlamp as motoHeadlamp, motoPaint, taillamp as motoTaillamp } from './Motorbike.js';
import { createParkedScooter, scooterPaint } from './Scooter.js';
import type { LampSpot } from './carLights.js';
import { takeLamps } from './carLights.js';

export { GLSL_LAMP_LIGHT, addStreetLampObjectShader, applyStreetLampLight, floraNight, lampLight, updateLampLightMap };

/**
 * A parked car baked flat, its body painted by the instance colour: a car's
 * plain outline (`createParkedCar`), a truck or bus the drivable model.
 * Without its tailpipe: a parked petrol or diesel car gets one of its own
 * (see `buildExhausts`), an electric one none.
 */
function parkedCar(kind: CarKind): BufferGeometry {
  const car = isTruck(kind) || isBus(kind) ? createVehicle(kind, 0xffffff, true, false) : createParkedCar(kind);
  return bakeModel(car.root, new Set([car.paint]));
}

function parkedMotorbike(): BufferGeometry {
  return bakeModel(createParkedMotorbike(), new Set([motoPaint]));
}

function parkedScooter(): BufferGeometry {
  return bakeModel(createParkedScooter(), new Set([scooterPaint]));
}

/** De-index, give every facet its own normal, and paint it one colour. */
function paint(geometry: BufferGeometry, hex: number): BufferGeometry {
  const flat = geometry.index === null ? geometry : geometry.toNonIndexed();
  if (flat !== geometry) geometry.dispose();
  if (flat.getAttribute('uv') !== undefined) flat.deleteAttribute('uv');
  flat.computeVertexNormals();
  const c = new Color(hex);
  const count = flat.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) colors.set([c.r, c.g, c.b], i * 3);
  flat.setAttribute('color', new BufferAttribute(colors, 3));
  flat.setAttribute('aGlow', new BufferAttribute(new Float32Array(count), 1));
  // Everything the instance colour tints: all of it, for a plant or a prop.
  flat.setAttribute('aTint', new BufferAttribute(new Float32Array(count).fill(1), 1));
  return flat;
}

/**
 * A bus shelter's parts, one by one, in its own frame (front facing -Z):
 * three panes of glass round its back and sides, two posts, the roof, a
 * seat, and the stop's sign on its own post. Merged, they are the shelter
 * as it stands; apart, they are what a car leaves of it (see
 * `three/shards.ts`).
 */
export function shelterParts(): Array<{ geometry: BufferGeometry; glass: boolean }> {
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, hex: number) =>
    paint(new BoxGeometry(w, h, d).translate(x, y, z), hex);
  const post = (r: number, h: number, x: number, y: number, z: number, hex: number) =>
    paint(new CylinderGeometry(r, r, h, 6).translate(x, y + h / 2, z), hex);
  const GLASS = 0x6f8e9a, FRAME = 0x3d4449;
  return [
    { geometry: glowing(box(3.2, 2.0, 0.05, 0, 1.25, 0.7, GLASS), 0.1), glass: true },
    { geometry: glowing(box(0.05, 2.0, 1.4, -1.58, 1.25, 0, GLASS), 0.1), glass: true },
    { geometry: glowing(box(0.05, 2.0, 1.4, 1.58, 1.25, 0, GLASS), 0.1), glass: true },
    { geometry: post(0.05, 2.3, -1.6, 0, 0.7, FRAME), glass: false },
    { geometry: post(0.05, 2.3, 1.6, 0, 0.7, FRAME), glass: false },
    { geometry: box(3.5, 0.12, 1.7, 0, 2.36, 0, FRAME), glass: false },
    { geometry: glowing(box(2.6, 0.06, 0.4, 0, 0.5, 0.45, 0x9a6b3e), 0.14), glass: false },
    { geometry: post(0.05, 2.9, 1.9, 0, -0.3, 0x5b6268), glass: false },
    { geometry: box(0.5, 0.5, 0.04, 1.9, 2.75, -0.3, 0x2f6fb0), glass: false },
  ];
}

/** Mark a painted part as a light source at night — a lamp's lens, or a surface catching its glow. */
function glowing(geometry: BufferGeometry, amount = 1): BufferGeometry {
  (geometry.getAttribute('aGlow').array as Float32Array).fill(amount);
  return geometry;
}

/** Paint by facing: faces turned up to the sky take `up`, the rest `side`. */
function paintByFacing(geometry: BufferGeometry, up: number, side: number, threshold = 0.3): BufferGeometry {
  const flat = paint(geometry, side);
  const normal = flat.getAttribute('normal');
  const color = flat.getAttribute('color');
  const snow = new Color(up);
  for (let i = 0; i < normal.count; i++) {
    if (normal.getY(i) > threshold) color.setXYZ(i, snow.r, snow.g, snow.b);
  }
  return flat;
}

/**
 * A row of flat pointed pickets along X, `length` long, one every `spacing`,
 * each `width` wide and `height` tall to its tip, set `out` along +Z: one
 * pentagon of three triangles each.
 */
function pickets(length: number, spacing: number, width: number, height: number, out: number): BufferGeometry {
  const n = Math.round(length / spacing);
  const positions: number[] = [];
  const shoulder = height - width * 0.8;
  for (let i = 0; i < n; i++) {
    const x = -length / 2 + (i + 0.5) * (length / n), w = width / 2;
    const bl = [x - w, -0.1, out], br = [x + w, -0.1, out], sr = [x + w, shoulder, out], tip = [x, height, out], sl = [x - w, shoulder, out];
    positions.push(...bl, ...br, ...sr, ...bl, ...sr, ...sl, ...sl, ...sr, ...tip);
  }
  const g = new Geometry();
  g.setAttribute('position', new Float32BufferAttribute(positions, 3));
  return paint(g, 0xffffff);
}

function merge(parts: BufferGeometry[]): BufferGeometry {
  const merged = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  return merged;
}

const BARK = 0x7a4f30;
const PALE_BARK = 0x9a7a52;

function trunk(rTop: number, rBottom: number, height: number, hex = BARK, sides = 5): BufferGeometry {
  return paint(new CylinderGeometry(rTop, rBottom, height, sides).translate(0, height / 2, 0), hex);
}

function blob(sx: number, sy: number, sz: number, x: number, y: number, z: number, hex: number, turn = 0): BufferGeometry {
  return paint(new IcosahedronGeometry(1, 0).scale(sx, sy, sz).rotateY(turn).translate(x, y, z), hex);
}

function conifer(radii: number[], heights: number[], ys: number[], sides: number, colour: (g: BufferGeometry) => BufferGeometry): BufferGeometry[] {
  return radii.map((r, i) => colour(new ConeGeometry(r, heights[i], sides).translate(0, ys[i], 0)));
}

/** A palm frond: a drooping, tapering blade, both sides drawn by the material. */
function frond(angle: number, length: number, droop: number): BufferGeometry {
  const segments = 4;
  const positions: number[] = [];
  const dx = Math.cos(angle);
  const dz = Math.sin(angle);
  const px = -dz;
  const pz = dx;
  const at = (t: number, side: number): [number, number, number] => {
    const width = (0.62 * Math.sin(Math.PI * Math.min(1, t * 1.15)) + 0.06) * side;
    return [dx * length * t + px * width, 0.9 * t * length * 0.35 - droop * t * t, dz * length * t + pz * width];
  };
  for (let s = 0; s < segments; s++) {
    const t0 = s / segments;
    const t1 = (s + 1) / segments;
    const a = at(t0, -1), b = at(t0, 1), c = at(t1, -1), d = at(t1, 1);
    // A crease down the midrib: each segment is two triangles meeting on it.
    const m0: [number, number, number] = [dx * length * t0, at(t0, 0)[1] + 0.08, dz * length * t0];
    const m1: [number, number, number] = [dx * length * t1, at(t1, 0)[1] + 0.08, dz * length * t1];
    positions.push(...a, ...m0, ...c, ...c, ...m0, ...m1, ...m0, ...b, ...m1, ...m1, ...b, ...d);
  }
  const g = new Geometry();
  g.setAttribute('position', new Float32BufferAttribute(positions, 3));
  return g;
}

function buildSpecies(): Record<Species, BufferGeometry> {
  const snowy = (g: BufferGeometry) => paintByFacing(g, 0xf1f5fa, 0x2a6a3e, 0.28);
  const snowyPine = (g: BufferGeometry) => paintByFacing(g, 0xf1f5fa, 0x2f8a3a, 0.3);
  // A winter crown: the trunk and a spray of bare branches where the leaves were.
  const bareCrown = (height: number, rTop: number, rBottom: number, spread: number, bark: number) => {
    const parts = [trunk(rTop, rBottom, height, bark, 6)];
    for (let k = 0; k < 7; k++) {
      const turn = (k / 7) * Math.PI * 2;
      const tilt = 0.55 + (k % 3) * 0.15;
      const length = spread * (0.8 + (k % 2) * 0.35);
      const y = height * (0.55 + (k % 4) * 0.1);
      parts.push(paint(new CylinderGeometry(0.03, 0.08, length, 4).translate(0, length / 2, 0)
        .rotateZ(tilt).rotateY(turn).translate(0, y, 0), bark));
    }
    return parts;
  };
  const green = (hex: number) => (g: BufferGeometry) => paint(g, hex);

  // Palm: a curving, ringed trunk and a crown of drooping fronds.
  const palmParts: BufferGeometry[] = [];
  let px = 0;
  let py = 0;
  for (let k = 0; k < 7; k++) {
    const seg = new CylinderGeometry(0.2, 0.25, 1.35, 6).translate(0, 0.675, 0).rotateZ(-0.05 * k).translate(px, py, 0);
    palmParts.push(paint(seg, k % 2 === 0 ? PALE_BARK : 0x86683f));
    px += Math.sin(0.05 * k) * 1.35;
    py += Math.cos(0.05 * k) * 1.3;
  }
  for (let f = 0; f < 8; f++) {
    palmParts.push(paint(frond((f / 8) * Math.PI * 2 + 0.2, 3.8 + (f % 3) * 0.4, 2.2).translate(px, py, 0), f % 2 ? 0x3f9a36 : 0x4fae3a));
  }
  for (let c = 0; c < 3; c++) {
    palmParts.push(blob(0.2, 0.2, 0.2, px + Math.cos(c * 2.1) * 0.28, py - 0.2, Math.sin(c * 2.1) * 0.28, 0x6b4a2a));
  }

  // Birch: a pale trunk with dark bands and a light, open crown.
  const birchParts: BufferGeometry[] = [trunk(0.13, 0.2, 8.5, 0xeeeae0, 6)];
  for (const y of [1.2, 2.6, 3.7, 5.1]) {
    birchParts.push(paint(new CylinderGeometry(0.19, 0.2, 0.12, 6).translate(0, y, 0), 0x2f2a26));
  }
  birchParts.push(
    blob(1.9, 2.4, 1.8, 0, 7.4, 0, 0x7cbb47),
    blob(1.4, 1.7, 1.5, 0.7, 9.1, -0.3, 0x8cc552, 0.7),
    blob(1.3, 1.5, 1.3, -0.9, 6.3, 0.5, 0x6fb041, 1.4),
  );

  // Grown Scots pine: a long trunk, grey-brown and furrowed low, thin and
  // orange where the bark flakes higher up, bare to two thirds of its height;
  // a few limbs out to a crown of flat, uneven clumps, flattest at the top.
  // Drawn slender: scaled up to a 25 m pine it is about 8 m across.
  const tallPineParts = (clump: (g: BufferGeometry) => BufferGeometry) => {
    const parts: BufferGeometry[] = [
      trunk(0.11, 0.16, 5.4, 0x6a5040, 6),
      paint(new CylinderGeometry(0.045, 0.11, 5.8, 6).translate(0, 5.4 + 2.9, 0), 0xb8693a),
    ];
    const clumps: Array<[number, number, number, number, number, number, number]> = [
      [2.25, 1.0, 2.05, 0.2, 10.5, 0.1, 0],
      [1.85, 0.9, 1.7, 1.45, 9.4, -0.6, 0.5],
      [1.75, 0.85, 1.6, -1.35, 9.0, 0.9, 1.1],
      [1.5, 0.75, 1.4, -0.5, 8.0, -1.4, 1.9],
      [1.3, 0.7, 1.25, 1.1, 7.4, 1.2, 2.6],
      [1.2, 0.65, 1.1, -1.5, 7.7, -0.3, 3.3],
    ];
    for (const [sx, sy, sz, x, y, z, turn] of clumps) {
      parts.push(clump(new IcosahedronGeometry(1, 0).scale(sx, sy, sz).rotateY(turn).translate(x, y, z)));
      // The limb up and out to it, from lower on the trunk.
      const out = Math.hypot(x, z);
      if (out < 0.3) continue;
      const rise = 1.2, length = Math.hypot(out, rise);
      parts.push(paint(new CylinderGeometry(0.03, 0.06, length, 4).translate(0, length / 2, 0)
        .rotateZ(-Math.atan2(out, rise)).rotateY(Math.atan2(-z, x)).translate(0, y - rise, 0), 0x8a5a36));
    }
    return parts;
  };

  // Jungle giant: buttressed trunk and a high, flat, layered crown.
  const jungleParts: BufferGeometry[] = [trunk(0.36, 0.6, 13.5, 0x6d5a40, 6)];
  for (let b = 0; b < 3; b++) {
    jungleParts.push(paint(new BoxGeometry(0.12, 2.2, 1.4).translate(0, 1.1, 0.75).rotateY((b / 3) * Math.PI * 2), 0x6d5a40));
  }
  jungleParts.push(
    blob(5.2, 2.2, 5.0, 0, 13.6, 0, 0x2b7a2a),
    blob(3.8, 1.8, 3.6, 2.0, 15.2, 1.0, 0x359032, 0.6),
    blob(3.4, 1.7, 3.6, -2.2, 14.7, -1.3, 0x247026, 1.2),
    blob(2.4, 1.4, 2.4, -0.5, 10.4, 2.2, 0x2f852d, 2.1),
  );

  // Acacia: a forked trunk under a wide, flat parasol.
  const acaciaParts: BufferGeometry[] = [trunk(0.18, 0.28, 3.2, 0x6e5238)];
  for (const [angle, turn] of [[0.55, 0], [-0.5, 2.1], [0.45, 4.0]]) {
    acaciaParts.push(paint(new CylinderGeometry(0.11, 0.16, 2.4, 5).translate(0, 1.2, 0).rotateZ(angle).rotateY(turn).translate(0, 3.0, 0), 0x6e5238));
  }
  acaciaParts.push(
    blob(4.8, 0.9, 4.5, 0, 5.2, 0, 0x6f913a),
    blob(3.2, 0.7, 3.0, 1.2, 5.8, -0.8, 0x7ea144, 0.8),
  );

  // Saguaro: a ribbed column with two arms.
  const cactusGreen = 0x4b8a3c;
  const cactusParts: BufferGeometry[] = [
    paint(new CylinderGeometry(0.34, 0.38, 5.4, 8).translate(0, 2.7, 0), cactusGreen),
    paint(new ConeGeometry(0.34, 0.4, 8).translate(0, 5.6, 0), cactusGreen),
    paint(new CylinderGeometry(0.22, 0.22, 0.9, 7).rotateZ(Math.PI / 2).translate(-0.7, 2.5, 0), cactusGreen),
    paint(new CylinderGeometry(0.22, 0.24, 1.9, 7).translate(-1.1, 3.4, 0), cactusGreen),
    paint(new ConeGeometry(0.22, 0.25, 7).translate(-1.1, 4.45, 0), cactusGreen),
    paint(new CylinderGeometry(0.2, 0.2, 0.7, 7).rotateZ(Math.PI / 2).translate(0.6, 3.3, 0), cactusGreen),
    paint(new CylinderGeometry(0.2, 0.22, 1.3, 7).translate(0.95, 3.9, 0), cactusGreen),
    paint(new ConeGeometry(0.2, 0.22, 7).translate(0.95, 4.65, 0), cactusGreen),
  ];

  // --- street furniture and fixtures, fronts facing -Z --------------------
  const box = (w: number, h: number, d: number, x: number, y: number, z: number, hex: number, turnX = 0) =>
    paint(new BoxGeometry(w, h, d).rotateX(turnX).translate(x, y, z), hex);
  const post = (r: number, h: number, x: number, y: number, z: number, hex: number, sides = 6) =>
    paint(new CylinderGeometry(r, r, h, sides).translate(x, y + h / 2, z), hex);
  const WOOD = 0x9a6b3e;
  const IRON = 0x3a3f44;
  const STONE = 0xc2bcae;

  const bench = [
    box(1.8, 0.07, 0.46, 0, 0.45, 0, WOOD),
    box(1.8, 0.36, 0.05, 0, 0.72, 0.22, WOOD, -0.18),
    box(0.06, 0.45, 0.46, -0.78, 0.22, 0, IRON), box(0.06, 0.45, 0.46, 0.78, 0.22, 0, IRON),
  ];
  const lamp = [
    glowing(post(0.08, 6.2, 0, 0, 0, 0x4d565c), 0.12),
    glowing(box(0.08, 0.08, 1.5, 0, 6.1, -0.72, 0x4d565c), 0.24),
    glowing(box(0.36, 0.12, 0.54, 0, 6.04, -1.4, 0x4d565c), 0.2),
    glowing(box(0.3, 0.11, 0.48, 0, 5.93, -1.4, 0xfff5cc), 1.0),
  ];
  const shelter = shelterParts().map(p => p.geometry);
  const fountainParts = [
    paint(new CylinderGeometry(2.2, 2.35, 0.55, 8).translate(0, 0.27, 0), STONE),
    paint(new CylinderGeometry(2.0, 2.0, 0.05, 8).translate(0, 0.48, 0), 0x5fb8d6),
    post(0.28, 1.3, 0, 0.45, 0, STONE, 8),
    paint(new CylinderGeometry(0.85, 0.5, 0.25, 8).translate(0, 1.8, 0), STONE),
    paint(new CylinderGeometry(0.75, 0.75, 0.04, 8).translate(0, 1.9, 0), 0x6cc3e0),
    paint(new ConeGeometry(0.12, 0.7, 5).translate(0, 2.25, 0), 0xcfeef8),
  ];
  const swing = [
    ...[-1.6, 1.6].flatMap(x => [
      paint(new CylinderGeometry(0.06, 0.06, 2.6, 5).rotateX(0.32).translate(x, 1.25, -0.4), 0xd84a3a),
      paint(new CylinderGeometry(0.06, 0.06, 2.6, 5).rotateX(-0.32).translate(x, 1.25, 0.4), 0xd84a3a),
    ]),
    paint(new CylinderGeometry(0.06, 0.06, 3.3, 5).rotateZ(Math.PI / 2).translate(0, 2.45, 0), 0xd84a3a),
    ...[-0.7, 0.7].flatMap(x => [
      box(0.02, 1.9, 0.02, x - 0.2, 1.5, 0, 0x555555), box(0.02, 1.9, 0.02, x + 0.2, 1.5, 0, 0x555555),
      box(0.5, 0.05, 0.22, x, 0.55, 0, 0x2f6fb0),
    ]),
  ];
  const slide = [
    box(1.0, 0.08, 1.0, 0, 1.5, 0.8, 0xf3c33c),
    post(0.05, 1.5, -0.45, 0, 0.35, 0xd84a3a), post(0.05, 1.5, 0.45, 0, 0.35, 0xd84a3a),
    post(0.05, 1.5, -0.45, 0, 1.25, 0xd84a3a), post(0.05, 1.5, 0.45, 0, 1.25, 0xd84a3a),
    box(0.7, 0.05, 3.0, 0, 0.8, -1.0, 0x3aa0d8, -0.5),
    box(0.6, 1.6, 0.06, 0, 0.8, 1.35, 0xf3c33c, 0.25),
  ];
  // A goal as a frame: posts, crossbar, and the net's supports behind. A
  // solid net panel reads as a white box from any distance, so the net is
  // only its outline.
  const NET = 0x9aa0a4;
  const goal = [
    post(0.06, 2.44, -3.66, 0, 0, 0xffffff), post(0.06, 2.44, 3.66, 0, 0, 0xffffff),
    paint(new CylinderGeometry(0.06, 0.06, 7.44, 6).rotateZ(Math.PI / 2).translate(0, 2.44, 0), 0xffffff),
    post(0.025, 1.4, -3.66, 0, 1.8, NET), post(0.025, 1.4, 3.66, 0, 1.8, NET),
    box(7.32, 0.03, 0.03, 0, 1.4, 1.8, NET),
    box(7.32, 0.03, 0.03, 0, 0.02, 1.8, NET),
    ...[-3.66, 3.66].flatMap(x => [
      paint(new BoxGeometry(0.03, 0.03, 2.0).rotateX(Math.atan2(1.04, 1.8)).translate(x, 1.92, 0.9), NET),
      box(0.03, 0.03, 1.8, x, 0.02, 0.9, NET),
    ]),
  ];
  const tennisNet = [
    post(0.05, 1.07, 0, 0, -6.4, 0x2a2e30), post(0.05, 1.07, 0, 0, 6.4, 0x2a2e30),
    box(0.02, 0.86, 12.8, 0, 0.5, 0, 0x2a2e30),
    box(0.04, 0.06, 12.8, 0, 0.94, 0, 0xffffff),
  ];

  const tuftShape = () => merge([0, 1, 2].map(i => paint(new ConeGeometry(0.23, 1.1 + i * 0.2, 3)
    .rotateZ((i - 1) * 0.35).translate((i - 1) * 0.35, 0.45, 0), 0xffffff)));

  const models: Record<Species, BufferGeometry[]> = {
    pine: [trunk(0.22, 0.34, 3.2), ...conifer([3.5, 2.8, 1.9], [5.4, 4.8, 4.2], [4.5, 7, 9.3], 5, green(0x2f8a3a))],
    spruce: [trunk(0.18, 0.28, 2.4), ...conifer([2.6, 2.1, 1.6, 1.0], [4.2, 3.8, 3.4, 3.0], [4.0, 6.4, 8.6, 10.6], 7, green(0x256b3a))],
    snowySpruce: [trunk(0.18, 0.28, 2.4), ...conifer([2.5, 2.0, 1.5, 0.95], [4.0, 3.6, 3.2, 2.8], [3.8, 6.1, 8.2, 10.1], 7, snowy)],
    birch: birchParts,
    broadleaf: [
      trunk(0.25, 0.38, 5.2),
      blob(3.4, 3.2, 3.2, 0, 6.4, 0, 0x4f9f33),
      blob(2.7, 2.6, 2.6, 0.5, 9.0, -0.3, 0x5fae3a, 0.6),
      blob(2.2, 2.1, 2.2, -2.1, 7.2, 1.0, 0x448f30, 1.3),
    ],
    jungle: jungleParts,
    palm: palmParts,
    acacia: acaciaParts,
    cactus: cactusParts,
    olive: [
      paint(new CylinderGeometry(0.14, 0.26, 2.2, 5).translate(0, 1.1, 0).rotateZ(0.25), 0x6a5a48),
      paint(new CylinderGeometry(0.12, 0.2, 1.8, 5).translate(0, 0.9, 0).rotateZ(-0.35).translate(0.2, 0.9, 0), 0x6a5a48),
      blob(1.9, 1.3, 1.8, 0.2, 3.1, 0, 0x8aa05c),
      blob(1.5, 1.1, 1.5, -0.9, 3.6, 0.6, 0x97aa68, 0.9),
      blob(1.3, 1.0, 1.4, 1.0, 3.8, -0.5, 0x7f9656, 1.7),
    ],
    cypress: [
      trunk(0.16, 0.22, 1.2, 0x5b4630),
      paint(new ConeGeometry(1.15, 9.5, 7).translate(0, 5.8, 0), 0x2d5e34),
      paint(new IcosahedronGeometry(1, 0).scale(1.25, 2.6, 1.25).translate(0, 3.2, 0), 0x2a5831),
    ],
    shrub: [blob(1.3, 0.9, 1.1, 0, 0.65, 0, 0x3f8f33), blob(0.9, 0.7, 0.9, 0.8, 0.5, 0.3, 0x4f9f38, 1)],
    dryShrub: [blob(0.95, 0.6, 0.9, 0, 0.5, 0, 0x8e8746), blob(0.7, 0.5, 0.7, 0.6, 0.4, -0.3, 0x9d9352, 1.1)],
    rock: [blob(1.6, 1.1, 1.3, 0, 0.5, 0, 0xffffff)],
    tuft: [tuftShape()],
    dryTuft: [(() => { const g = tuftShape(); const c = g.getAttribute('color'); const dry = new Color(0xc8b46a);
      for (let i = 0; i < c.count; i++) c.setXYZ(i, dry.r, dry.g, dry.b); return g; })()],
    stump: [trunk(0.35, 0.5, 0.85, 0x95734e)],
    bench,
    bin: [post(0.26, 0.85, 0, 0, 0, 0x2f5a3a, 8), paint(new CylinderGeometry(0.29, 0.29, 0.06, 8).translate(0, 0.88, 0), 0x24452d)],
    postbox: [post(0.06, 1.0, 0, 0, 0, IRON), box(0.4, 0.5, 0.3, 0, 1.2, 0, 0xf2c230), box(0.42, 0.06, 0.32, 0, 1.47, 0, 0x2f6fb0)],
    lamp,
    shelter,
    fountain: fountainParts,
    memorial: [
      box(1.3, 0.4, 1.3, 0, 0.2, 0, STONE),
      paint(new CylinderGeometry(0.2, 0.42, 3.2, 4, 1, false, Math.PI / 4).translate(0, 2.0, 0), 0xb3ad9f),
      paint(new ConeGeometry(0.2, 0.4, 4, 1, false, Math.PI / 4).translate(0, 3.8, 0), 0xb3ad9f),
    ],
    tower: [
      paint(new CylinderGeometry(0.7, 2.2, 28, 4, 1, false, Math.PI / 4).translate(0, 14, 0), 0xd9d9d6),
      box(3.2, 0.5, 3.2, 0, 26, 0, 0xc0504a),
      paint(new CylinderGeometry(0.08, 0.08, 6, 4).translate(0, 31, 0), 0xc0504a),
    ],
    gravestone: [box(0.62, 0.12, 0.3, 0, 0.06, 0, 0x8f8a80), box(0.5, 0.8, 0.1, 0, 0.5, 0, 0xa9a59c)],
    swing,
    slide,
    goal,
    tennisNet,
    // A garden's low clipped hedge: 4 m pieces along local X, sunk a little so
    // a run stepping down a slope has no gap under it. See `core/build/plots.ts`.
    hedge: [box(4.1, 1.15, 0.7, 0, 0.3, 0, 0x2f6d2b), box(3.98, 0.12, 0.56, 0, 0.9, 0, 0x3d8436)],
    // Painted white: the instance colour paints them — white, or the house's.
    // Rails and pickets are flat (the material is two-sided): a fence is seen
    // from the street, and a board's edge is a hair wide from there.
    // A picket fence: posts, two rails, and pointed pickets on the street side.
    picket: [
      ...[-2, 2].map(x => box(0.11, 1.3, 0.11, x, 0.4, 0, 0xffffff)),
      ...[0.3, 0.78].map(y => paint(new PlaneGeometry(4, 0.08).translate(0, y, 0.06), 0xededed)),
      pickets(4, 0.3, 0.09, 1.0, 0.075),
    ],
    // Post and rail: square posts and three open boards.
    rail: [
      ...[-2, 2].map(x => box(0.11, 1.3, 0.11, x, 0.4, 0, 0xffffff)),
      ...[0.35, 0.65, 0.95].map(y => paint(new PlaneGeometry(4, 0.1).translate(0, y, 0.06), 0xf4f4f4)),
    ],
    // Seasonal states: the same trees, coloured or bare.
    broadleafAutumn: [
      trunk(0.25, 0.38, 5.2),
      blob(3.4, 3.2, 3.2, 0, 6.4, 0, 0xd9772a),
      blob(2.7, 2.6, 2.6, 0.5, 9.0, -0.3, 0xe8a136, 0.6),
      blob(2.2, 2.1, 2.2, -2.1, 7.2, 1.0, 0xb9452a, 1.3),
    ],
    broadleafBare: bareCrown(5.2, 0.25, 0.38, 3.2, BARK),
    birchAutumn: [
      trunk(0.13, 0.2, 8.5, 0xeeeae0, 6),
      ...[1.2, 2.6, 3.7, 5.1].map(y => paint(new CylinderGeometry(0.19, 0.2, 0.12, 6).translate(0, y, 0), 0x2f2a26)),
      blob(1.9, 2.4, 1.8, 0, 7.4, 0, 0xf0c23a),
      blob(1.4, 1.7, 1.5, 0.7, 9.1, -0.3, 0xe7a92e, 0.7),
      blob(1.3, 1.5, 1.3, -0.9, 6.3, 0.5, 0xf5d352, 1.4),
    ],
    birchBare: bareCrown(8.5, 0.13, 0.2, 2.2, 0xeeeae0),
    pineSnowy: [trunk(0.22, 0.34, 3.2), ...conifer([3.5, 2.8, 1.9], [5.4, 4.8, 4.2], [4.5, 7, 9.3], 5, snowyPine)],
    tallPine: tallPineParts(green(0x2e6a33)),
    tallPineSnowy: tallPineParts(g => paintByFacing(g, 0xf1f5fa, 0x2e6a33, 0.3)),
    carHatch: [parkedCar('hatch')],
    carSedan: [parkedCar('sedan')],
    carEstate: [parkedCar('estate')],
    carSuv: [parkedCar('suv')],
    carVan: [parkedCar('van')],
    carPickup: [parkedCar('pickup')],
    parkedMoto: [parkedMotorbike()],
    truckBox: [parkedCar('truck')],
    truckSemi: [parkedCar('semi')],
    busArea: [parkedCar('busArea')],
    busExpress: [parkedCar('busExpress')],
    busMetro: [parkedCar('busMetro')],
    busCity: [parkedCar('busCity')],
    parkedScooter: [parkedScooter()],
  };

  return Object.fromEntries(
    SPECIES.map(s => [s, swayable(merge(models[s]), SWAY[s] ?? [0, 0])]),
  ) as Record<Species, BufferGeometry>;
}

/**
 * How each species gives to the wind: [bend, flutter].
 *
 * `bend` scales how far the stem bows, relative to a typical broadleaf. A
 * slender birch or a palm bows further, and a massive rainforest giant or a
 * stiff olive less. A bare winter tree has little sail, so it bows less too.
 * Grass is all give. `flutter` is how much the outer crown shimmers on top of
 * that: broad leaves and fronds a lot, needles little. Anything missing, like
 * rocks, cactus and street furniture, stands still.
 */
const SWAY: Partial<Record<Species, readonly [number, number]>> = {
  pine: [0.8, 0.35], spruce: [0.7, 0.3], snowySpruce: [0.55, 0.15], pineSnowy: [0.6, 0.15],
  tallPine: [0.7, 0.3], tallPineSnowy: [0.5, 0.15],
  birch: [1.35, 1], broadleaf: [1, 0.9], broadleafAutumn: [1, 1], broadleafBare: [0.6, 0.25],
  birchAutumn: [1.35, 1], birchBare: [0.8, 0.3],
  jungle: [0.45, 0.7], palm: [1.5, 1.4], acacia: [0.65, 0.6], olive: [0.55, 0.7], cypress: [0.9, 0.3],
  shrub: [0.7, 0.8], dryShrub: [0.55, 0.6], tuft: [3.2, 0.6], dryTuft: [3.2, 0.6], hedge: [0.12, 0.5],
};

/**
 * Bake the wind response into the geometry as `aSway` = (bend weight,
 * flutter weight, model height).
 *
 * A stem loaded along its length bows about as the square of the height up
 * it, so the base stays put and the top moves most. Flutter grows toward the
 * outside of the crown, where the leaves are on the thinnest twigs. Both
 * depend only on a vertex's position, so the duplicated corners of flat
 * shaded faces move together and never crack apart.
 */
function swayable(geometry: BufferGeometry, [bend, flutter]: readonly [number, number]): BufferGeometry {
  const position = geometry.getAttribute('position');
  const count = position.count;
  let height = 0;
  let radius = 0;
  for (let i = 0; i < count; i++) {
    height = Math.max(height, position.getY(i));
    radius = Math.max(radius, Math.hypot(position.getX(i), position.getZ(i)));
  }
  const sway = new Float32Array(count * 3);
  if (bend > 0 && height > 0) {
    for (let i = 0; i < count; i++) {
      const h = Math.max(0, position.getY(i)) / height;
      const r = radius > 0 ? Math.hypot(position.getX(i), position.getZ(i)) / radius : 0;
      sway[i * 3] = bend * h * h;
      sway[i * 3 + 1] = flutter * Math.min(1, r * 1.2) * h;
      sway[i * 3 + 2] = height;
    }
  }
  geometry.setAttribute('aSway', new BufferAttribute(sway, 3));
  return geometry;
}

const GEOMETRY = buildSpecies();

const material = new MeshStandardMaterial({
  color: 0xffffff,
  vertexColors: true,
  flatShading: true,
  roughness: 1,
  metalness: 0,
  // Palm fronds are single sheets; everything else is closed and does not care.
  side: DoubleSide,
});

/** A car in traffic, baked as a parked one is but without its wheels, which turn and steer apart. */
export interface TrafficModel {
  body: BufferGeometry;
  /** Where each wheel's centre sits on the car, metres: x right, z forward-negative. */
  wheels: Array<{ x: number; z: number; front: boolean }>;
  wheelRadius: number;
  /** Its lamps, left out of `body` for the traffic to draw lit (see `carLights.ts`). */
  lamps?: { heads: LampSpot[]; tails: LampSpot[] };
  /**
   * An articulated truck's trailer, drawn apart from its tractor so it can
   * swing: its body about the kingpin, its wheels, where the kingpin sits on
   * the tractor and how far back of it the axles turn it.
   */
  trailer?: {
    body: BufferGeometry;
    wheels: Array<{ x: number; z: number }>;
    wheelRadius: number;
    kingpinZ: number;
    axleZ: number;
    length: number;
    width: number;
    lamps?: { heads: LampSpot[]; tails: LampSpot[] };
  };
}

const trafficModels = new Map<CarKind, TrafficModel>();
let trafficWheelGeometry: { left: BufferGeometry; right: BufferGeometry; radius: number } | undefined;

export function trafficModel(kind: CarKind): TrafficModel {
  let model = trafficModels.get(kind);
  if (model === undefined) {
    const car = createVehicle(kind, 0xffffff, true, false);
    for (const wheel of car.wheels) wheel.pivot.visible = false;
    const tinted = new Set([car.paint]);
    // A trailer is baked on its own, at the kingpin, straight.
    const t = car.trailer;
    let trailer: TrafficModel['trailer'];
    if (t !== undefined) {
      for (const wheel of t.wheels) wheel.pivot.visible = false;
      t.root.removeFromParent();
      t.root.position.set(0, 0, 0);
      const holder = new Object3D();
      holder.add(t.root);
      const trailerLamps = takeLamps(holder, [headlamp], [taillamp]);
      trailer = {
        lamps: trailerLamps,
        body: swayable(bakeModel(holder, tinted), [0, 0]),
        wheels: t.wheels.map(w => ({ x: w.x, z: w.z })),
        wheelRadius: t.wheelRadius, kingpinZ: t.kingpinZ, axleZ: t.axleZ, length: t.length, width: t.width,
      };
    }
    // The lamps are the traffic's to draw, lit: left out of the bake.
    const lamps = takeLamps(car.root, [headlamp], [taillamp]);
    model = {
      lamps,
      body: swayable(bakeModel(car.root, tinted), [0, 0]),
      wheels: car.wheels.map(w => ({ x: w.x, z: w.z, front: w.front })),
      wheelRadius: car.wheelRadius,
      trailer,
    };
    trafficModels.set(kind, model);
  }
  return model;
}

/**
 * The motorbike as the traffic draws it: baked whole, wheels and all, the
 * plastics tinted by the instance (or a livery). Its wheels do not turn;
 * from a car's distance nobody sees.
 */
export function trafficMotorbike(): TrafficModel {
  const root = createMotorbike().root;
  const lamps = takeLamps(root, [motoHeadlamp], [motoTaillamp]);
  return { body: swayable(bakeModel(root, new Set([motoPaint])), [0, 0]), wheels: [], wheelRadius: 0.36, lamps };
}

/**
 * One wheel for each side of a car in traffic, centred on its axle, the rim
 * facing out: a hatchback's, scaled to fit the others.
 */
export function trafficWheels(): { left: BufferGeometry; right: BufferGeometry; radius: number } {
  if (trafficWheelGeometry === undefined) {
    const car = createVehicle('hatch', 0xffffff, true, false);
    const bake = (index: number) => {
      const holder = new Object3D();
      const spin = car.wheels[index].spin;
      spin.removeFromParent();
      spin.position.set(0, 0, 0);
      holder.add(spin);
      return swayable(bakeModel(holder, new Set()), [0, 0]);
    };
    const left = car.wheels.findIndex(w => w.x < 0), right = car.wheels.findIndex(w => w.x > 0);
    trafficWheelGeometry = { left: bake(left), right: bake(right), radius: car.wheelRadius };
  }
  return trafficWheelGeometry;
}

/**
 * An empty, movable instanced mesh drawn as the props are: for traffic,
 * whose instances are rewritten every frame. Instances are in scene
 * coordinates, so it is never culled as a whole.
 */
export function movingInstances(geometry: BufferGeometry, capacity: number, name: string, look: Material = moving): InstancedMeshType {
  const mesh = new InstancedMesh(geometry, look, capacity);
  mesh.name = name;
  mesh.customDepthMaterial = depthMaterial;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.setColorAt(0, new Color(1, 1, 1));
  mesh.instanceColor!.setUsage(DynamicDrawUsage);
  mesh.count = 0;
  mesh.frustumCulled = false;
  return mesh;
}

/** The wind the flora sways in, shared by every flora material. */
const windUniforms = {
  uWindDir: { value: new Vector2(1, 0) },
  uWindDrift: { value: new Vector2() },
  uWindSpeed: { value: 0 },
  uWindGust: { value: 0 },
  uWindTime: { value: 0 },
};

/** Hand the flora this frame's wind. */
export function setFloraWind(wind: WindState): void {
  windUniforms.uWindDir.value.set(wind.dir.x, wind.dir.z);
  windUniforms.uWindDrift.value.set(wind.drift.x, wind.drift.z);
  windUniforms.uWindSpeed.value = wind.speed;
  windUniforms.uWindGust.value = wind.gust;
  windUniforms.uWindTime.value = wind.time;
}

/**
 * The GLSL half of `core/weather/wind.ts`, applied to `transformed` in the
 * instance's own space.
 *
 * 1. **Gusts.** Two octaves of value noise, carried downwind by `uWindDrift`,
 *    are sampled at the tree's root. The pattern is stretched along the wind,
 *    because gusts are streaks rather than blobs. The noise picks the tree's
 *    wind speed between a lull (70% of the mean) and the reported peak gust,
 *    so a gust front rolls visibly across a forest.
 * 2. **Bend.** Tip deflection from `bendFraction`, which grows as v² and
 *    saturates. The tree swings about that lean at its own natural frequency,
 *    which is lower for taller trees, with a phase set by where it stands.
 *    The swing is larger when the wind is gustier. A smaller swing across the
 *    wind stands in for vortex shedding.
 * 3. **Flutter.** The crown's outer vertices shimmer quicker and harder as the
 *    wind picks up.
 *
 * The stem bows along an arc rather than shearing, so a bent tree gets a
 * little shorter.
 */
const WIND_VERTEX = /* glsl */ `
#ifdef USE_INSTANCING
  mat4 swayFrame = modelMatrix * instanceMatrix;
#else
  mat4 swayFrame = modelMatrix;
#endif
  if (aSway.x + aSway.y > 0.0) {
    vec2 root = swayFrame[3].xz;
    vec2 perp = vec2(-uWindDir.y, uWindDir.x);
    vec2 q = root - uWindDrift;
    vec2 g = vec2(dot(q, uWindDir) / 120.0, dot(q, perp) / 45.0) + uWindTime * 0.015;
    float n = windNoise(g) * 0.65 + windNoise(g * 2.3 + 7.1) * 0.35;
    float v = mix(uWindSpeed * 0.7, uWindGust, smoothstep(0.3, 0.85, n));
    float r = v / ${BEND_SPEED.toFixed(1)};
    float bend = ${MAX_BEND.toFixed(3)} * (1.0 - exp(-r * r));

    float treeHeight = aSway.z * length(swayFrame[1].xyz);
    float phase = windHash(root) * 6.2832;
    float gusty = clamp((uWindGust - uWindSpeed) / max(uWindSpeed, 1.0), 0.15, 1.0);
    float w = 6.2832 * (1.6 / sqrt(max(0.3, treeHeight))) * uWindTime + phase;
    float along = bend * (1.0 + 0.5 * gusty * sin(w));
    float across = bend * 0.25 * gusty * sin(w * 1.37 + 1.3);

    // World wind direction into the instance's frame. The frame is a yaw and
    // a near-uniform scale, so the transpose points the right way once
    // normalised.
    vec3 localAlong = transpose(mat3(swayFrame)) * vec3(uWindDir.x, 0.0, uWindDir.y);
    vec3 localAcross = transpose(mat3(swayFrame)) * vec3(perp.x, 0.0, perp.y);
    vec2 a = normalize(localAlong.xz + 1e-6);
    vec2 c = normalize(localAcross.xz + 1e-6);
    vec2 shift = (a * along + c * across) * aSway.x * aSway.z;
    transformed.xz += shift;
    transformed.y -= 0.5 * dot(shift, shift) / max(transformed.y, 0.25);

    float speed = 1.5 + v * 0.3;
    float amount = aSway.y * (0.012 + 0.009 * v) * (0.6 + 0.4 * gusty);
    vec3 fp = position * 1.7 + phase;
    float t = uWindTime * 6.2832 * speed;
    transformed += amount * vec3(
      sin(t + fp.x + fp.y * 1.3),
      0.5 * sin(t * 0.83 + fp.z * 1.9),
      cos(t * 0.91 + fp.y + fp.z * 1.1));
  }
`;

const WIND_HEADER = /* glsl */ `
uniform vec2 uWindDir;
uniform vec2 uWindDrift;
uniform float uWindSpeed;
uniform float uWindGust;
uniform float uWindTime;
attribute vec3 aSway;
attribute float aTint;
float windHash(vec2 p) {
  p = mod(p, 1024.0);
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float windNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(windHash(i), windHash(i + vec2(1.0, 0.0)), u.x),
             mix(windHash(i + vec2(0.0, 1.0)), windHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
`;

/** Make a material's vertices sway in the wind. The shadow pass needs it too. */
function addWind(shader: WebGLProgramParametersWithUniforms): void {
  Object.assign(shader.uniforms, windUniforms);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', `#include <common>\n${WIND_HEADER}`)
    // The instance colour tints only what the geometry flags: a car's body,
    // not its tyres. (The depth material has no colours; nothing to replace.)
    .replace('#include <color_vertex>', `#include <color_vertex>
#ifdef USE_INSTANCING_COLOR
  vColor.rgb = color * mix(vec3(1.0), instanceColor.rgb, aTint);
#endif`)
    .replace('#include <begin_vertex>', `#include <begin_vertex>\n${WIND_VERTEX}`);
}

/** Shadows cast by swaying flora, so they sway along with it. */
const depthMaterial = new MeshDepthMaterial();
depthMaterial.onBeforeCompile = addWind;

/** No snow settled: on what moves, which would shake it off or never stand long enough to gather it. */
const NO_SNOW = { value: 0 };

/**
 * Swaying in the wind, tinted by instance where the geometry says, and lit
 * at night where it glows or stands under a streetlamp; with snow on its
 * upper sides unless it moves (`settles` false).
 */
function floraShader(shader: WebGLProgramParametersWithUniforms, settles = true): void {
  addWind(shader);
  shader.uniforms.uNight = floraNight;
  shader.uniforms.uTreeSnow = settles ? terrainWeather.treeSnow : NO_SNOW;
  shader.uniforms.uLampMap = lampLight.map;
  shader.uniforms.uLampOrigin = lampLight.origin;
  shader.uniforms.uLampInvExtent = lampLight.invExtent;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute float aGlow;\nvarying float vGlow;\nvarying vec3 vFloraWorldPos;\nvarying float vFloraHeight;\nvarying float vFloraRough;')
    .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
  vGlow = aGlow;
  vFloraHeight = position.y;
  vFloraRough = mix(0.92, 0.32, aTint);
  vFloraWorldPos = (swayFrame * vec4(transformed, 1.0)).xyz;`);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>\nuniform float uNight;\nuniform float uTreeSnow;\nvarying float vGlow;\nvarying vec3 vFloraWorldPos;\nvarying float vFloraHeight;\nvarying float vFloraRough;\n${GLSL_LAMP_LIGHT}\n${GLSL_SNOW_TOP}`)
    .replace('#include <color_fragment>', `#include <color_fragment>
  // Snow settled on the branches' upper sides, a bench's seat, a parked car's roof.
  diffuseColor.rgb = mix(diffuseColor.rgb, ${SNOW_TOP_COLOUR}, snowTop(vFloraWorldPos, uTreeSnow, 0.32));`)
    .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  totalEmissiveRadiance += mix(vec3(1.0, 0.50, 0.15), vec3(1.0, 0.66, 0.26), vGlow) * (3.6 * vGlow * uNight);
  float floraHeightMask = smoothstep(5.8, 1.2, vFloraHeight) * (1.0 - step(0.01, vGlow));
  totalEmissiveRadiance += streetLampObjectRadiance(vFloraWorldPos, diffuseColor.rgb, uNight, vFloraRough, floraHeightMask);`);
}
material.onBeforeCompile = (shader) => floraShader(shader);

/** The flora's material for the traffic: the same, but no snow settles on a car driving along. */
const moving = material.clone();
moving.onBeforeCompile = (shader) => floraShader(shader, false);

/** A livery's layout and colours, as `liveried` paints them: see `core/traffic/police.ts`. */
export interface LiveryUniforms {
  uPattern: { value: number };
  uBase: { value: Color };
  uA: { value: Color };
  uB: { value: Color };
}

/** The police livery of the country the player is in: one for every police car, in traffic or not. Set by `Traffic.setLivery`. */
export const LIVERY: LiveryUniforms = {
  uPattern: { value: 0 }, uBase: { value: new Color(1, 1, 1) }, uA: { value: new Color(0, 0, 1) }, uB: { value: new Color(1, 1, 0) },
};

/**
 * The fragment code that paints a livery over `diffuseColor` where
 * `vLiveryTint` says the surface is paint, from `vLiveryAt` and
 * `vLiveryFacing`, the point and its facing in the car's own frame (x
 * across, y up from the ground, z along, nose toward -z). Laid out for a
 * saloon or an estate: sills at 0.33 m, the waistline at 0.93 m.
 */
export const LIVERY_HEADER = /* glsl */ `
uniform int uPattern;
uniform vec3 uBase;
uniform vec3 uA;
uniform vec3 uB;
varying vec3 vLiveryAt;
varying vec3 vLiveryFacing;
varying float vLiveryTint;`;
export const LIVERY_PAINT = /* glsl */ `
  if (vLiveryTint > 0.5) {
    vec3 p = vLiveryAt;
    bool side = abs(normalize(vLiveryFacing).x) > 0.55;
    vec3 paint = uBase;
    if (uPattern == 0) {
      // Battenburg: two rows of squares, sill to waistline.
      if (side && p.y > 0.33 && p.y < 0.93) {
        float row = step(0.63, p.y);
        float column = floor((p.z + 6.0) / 0.6);
        paint = mod(column + row, 2.0) < 0.5 ? uA : uB;
      }
    } else if (uPattern == 1) {
      // A band along the side, a pinstripe over it.
      if (side && p.y > 0.33 && p.y < 0.8) paint = uA;
      else if (side && p.y >= 0.8 && p.y < 0.86) paint = uB;
    } else if (uPattern == 2) {
      // Doors and roof of one colour, the rest of the other.
      paint = uA;
      if ((side && p.z > -0.95 && p.z < 1.35 && p.y < 0.97) || p.y > 1.3) paint = uB;
    } else {
      // A stripe along the side.
      if (side && p.y > 0.6 && p.y < 0.74) paint = uA;
    }
    diffuseColor.rgb = paint;
  }`;

/**
 * The traffic's material, no snow on it, but painting the police livery (`LIVERY`) over
 * the body where the instance colour would tint it: the pattern worked out
 * per fragment from the model's own position and facing, so a check is
 * crisp however the body's triangles fall. Sides are where the surface
 * faces across the car; the bonnet, roof and boot face up.
 */
export function liveried(): MeshStandardMaterial {
  const look = moving.clone();
  look.onBeforeCompile = (shader) => {
    floraShader(shader, false);
    Object.assign(shader.uniforms, LIVERY);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vLiveryAt;\nvarying vec3 vLiveryFacing;\nvarying float vLiveryTint;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vLiveryAt = position;\n  vLiveryFacing = normal;\n  vLiveryTint = aTint;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${LIVERY_HEADER}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${LIVERY_PAINT}`);
  };
  return look;
}

/**
 * Compact bulb bloom right around the lantern head at 5.93 m height. The light
 * on the ground itself is shaded directly on the road and terrain surfaces via
 * `uLampMap`, so nothing floats over the ground.
 */
const poolMaterial = new ShaderMaterial({
  uniforms: { uNight: floraNight },
  vertexShader: `varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: `uniform float uNight; varying vec2 vUv;
    void main() {
      if (uNight <= 0.001) discard;
      float r = length(vUv - 0.5) * 2.0;
      if (r >= 1.0) discard;
      float halo = pow(1.0 - r, 2.8) * 0.42;
      float hot = smoothstep(0.32, 0.0, r) * 0.58;
      float a = (halo + hot) * uNight;
      vec3 col = mix(vec3(1.0, 0.50, 0.14), vec3(1.0, 0.70, 0.30), clamp(hot, 0.0, 1.0));
      gl_FragColor = vec4(col, a);
    }`,
  transparent: true,
  depthWrite: false,
  side: DoubleSide,
  blending: AdditiveBlending,
});
const POOL = mergeGeometries([
  new PlaneGeometry(1.1, 0.85),
  new PlaneGeometry(1.1, 0.85).rotateY(Math.PI / 2),
  new PlaneGeometry(1.1, 1.1).rotateX(-Math.PI / 2),
]);

/** Lantern head blooms and ground light-map coordinates for a chunk's street lamps. */
export function buildLightPools(
  positions: Float32Array,
  rotations: Float32Array,
  species: Uint8Array,
): InstancedMeshType | undefined {
  const lamps: number[] = [];
  for (let i = 0; i < species.length; i++) if (species[i] === SPECIES_ID.lamp) lamps.push(i);
  if (lamps.length === 0) return undefined;
  const mesh = new InstancedMesh(POOL, poolMaterial, lamps.length);
  const mat = mesh.instanceMatrix.array;
  const lampHeads = new Float32Array(lamps.length * 2);
  const lampRoots = new Float32Array(lamps.length * 2);
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let k = 0; k < lamps.length; k++) {
    const i = lamps[k], i3 = i * 3;
    const yaw = rotations[i];
    const px = positions[i3], py = positions[i3 + 1], pz = positions[i3 + 2];
    // At the lantern head, which hangs 1.4 m out along the lamp's front at 5.93 m height.
    const x = px - Math.sin(yaw) * 1.4;
    const y = py + 5.93;
    const z = pz - Math.cos(yaw) * 1.4;
    lampHeads[k * 2] = x;
    lampHeads[k * 2 + 1] = z;
    lampRoots[k * 2] = px;
    lampRoots[k * 2 + 1] = pz;
    const o = k * 16;
    mat[o] = 1; mat[o + 1] = 0; mat[o + 2] = 0; mat[o + 3] = 0;
    mat[o + 4] = 0; mat[o + 5] = 1; mat[o + 6] = 0; mat[o + 7] = 0;
    mat[o + 8] = 0; mat[o + 9] = 0; mat[o + 10] = 1; mat[o + 11] = 0;
    mat[o + 12] = x; mat[o + 13] = y; mat[o + 14] = z; mat[o + 15] = 1;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  mesh.userData.lamps = lampHeads;
  mesh.userData.roots = lampRoots;
  mesh.instanceMatrix.needsUpdate = true;
  mesh.renderOrder = 4;
  const cx = (minX + maxX) * 0.5, cy = (minY + maxY) * 0.5, cz = (minZ + maxZ) * 0.5;
  mesh.boundingSphere = new Sphere(new Vector3(cx, cy, cz), Math.hypot(maxX - cx, maxY - cy, maxZ - cz) + 2);
  mesh.name = 'lamp-pools';
  return mesh;
}

/** Per-instance hue nudges for foliage, as multipliers near white. */
const TINTS = [
  [1, 1, 1], [0.9, 1.03, 0.88], [1.06, 1.03, 0.86], [0.86, 0.95, 0.92], [1.02, 0.97, 0.9],
];

export interface FloraTints {
  /** sRGB hex: the local rock colour, for boulders. */
  rock: number;
  /** sRGB hex: the local grass colour, for tufts. */
  grass: number;
}

/**
 * One instanced mesh per species present in the chunk.
 *
 * `positions` are x,y,z triples; `species` selects the model for each point.
 * Species absent from the chunk cost nothing — no mesh is created for them.
 */
export function buildFlora(
  positions: Float32Array,
  rotations: Float32Array,
  scales: Float32Array,
  species: Uint8Array,
  tints: FloraTints,
  paints?: Uint32Array,
  /** Forward and sideways slope per point: a parked car lies on the hill. */
  tilts?: Float32Array,
): InstancedMeshType[] {
  const buckets = new Map<number, number[]>();
  for (let i = 0; i < species.length; i++) {
    let list = buckets.get(species[i]);
    if (list === undefined) buckets.set(species[i], (list = []));
    list.push(i);
  }

  const dummy = new Object3D();
  const color = new Color();
  const rock = new Color(tints.rock);
  const grass = new Color(tints.grass);
  const meshes: InstancedMeshType[] = [];

  for (const [id, indices] of buckets) {
    const name = SPECIES[id];
    if (name === undefined) continue;
    const geom = GEOMETRY[name];
    const mesh = new InstancedMesh(geom, material, indices.length);
    mesh.name = `flora:${name}`;
    mesh.customDepthMaterial = depthMaterial;
    const prop = PROPS.has(name);
    const stretched = STRETCHED.has(name);
    const uniformScale = prop || name === 'rock' || name === 'tuft' || name === 'dryTuft' || name === 'shrub' || name === 'dryShrub';
    const mat = mesh.instanceMatrix.array;
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let maxS = 1;

    for (let k = 0; k < indices.length; k++) {
      const i = indices[k], i3 = i * 3;
      const s = scales[i];
      if (s > maxS) maxS = s;
      const px = positions[i3], py = positions[i3 + 1], pz = positions[i3 + 2];
      if (px < minX) minX = px; if (px > maxX) maxX = px;
      if (py < minY) minY = py; if (py > maxY) maxY = py;
      if (pz < minZ) minZ = pz; if (pz > maxZ) maxZ = pz;

      // Trees vary more in height than in spread, which reads as a stand of
      // mixed ages rather than one tree scaled up and down.
      const sx = stretched ? s : uniformScale ? s : 0.85 + s * 0.3;
      const sy = stretched ? 1 : s;
      const sz = stretched ? 1 : uniformScale ? s : 0.85 + s * 0.3;

      const t0 = tilts !== undefined ? tilts[i * 2] : 0;
      const t1 = tilts !== undefined ? tilts[i * 2 + 1] : 0;
      if (t0 === 0 && t1 === 0) {
        const yaw = rotations[i];
        const c = Math.cos(yaw), sn = Math.sin(yaw);
        const o = k * 16;
        mat[o] = c * sx; mat[o + 1] = 0; mat[o + 2] = -sn * sx; mat[o + 3] = 0;
        mat[o + 4] = 0; mat[o + 5] = sy; mat[o + 6] = 0; mat[o + 7] = 0;
        mat[o + 8] = sn * sz; mat[o + 9] = 0; mat[o + 10] = c * sz; mat[o + 11] = 0;
        mat[o + 12] = px; mat[o + 13] = py; mat[o + 14] = pz; mat[o + 15] = 1;
      } else {
        // Turned, then tipped to the slope: the order a car sits on a hill in
        // (see `orientToSlope`).
        dummy.position.set(px, py, pz);
        dummy.rotation.set(Math.atan(t0), rotations[i], Math.atan(t1), 'YXZ');
        dummy.scale.set(sx, sy, sz);
        dummy.updateMatrix();
        mesh.setMatrixAt(k, dummy.matrix);
      }

      const tint = TINTS[i % TINTS.length];
      const paintHex = paints?.[i] ?? 0;
      if (paintHex !== 0) color.setHex(paintHex & 0xffffff);
      else if (prop) color.setRGB(1, 1, 1);
      else if (name === 'rock') color.copy(rock).multiplyScalar(0.9 + (i % 5) * 0.05);
      else if (name === 'tuft') color.copy(grass).multiplyScalar(1.15);
      else color.setRGB(tint[0], tint[1], tint[2]);
      mesh.setColorAt(k, color);
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (geom.boundingSphere === null) geom.computeBoundingSphere();
    const geomRadius = (geom.boundingSphere?.radius ?? 5) * maxS + 2;
    const cx = (minX + maxX) * 0.5, cy = (minY + maxY) * 0.5, cz = (minZ + maxZ) * 0.5;
    mesh.boundingSphere = new Sphere(new Vector3(cx, cy, cz), Math.hypot(maxX - cx, maxY - cy, maxZ - cz) + geomRadius);
    meshes.push(mesh);
  }
  return meshes;
}
